/*
 * <twc-create-event> — the "Create your own event" band and its submission
 * modal (sections/twc-create-event-cta.liquid +
 * snippets/twc-modal-create-event.liquid).
 *
 * The section has already rendered everything: the band, the <dialog>, both
 * panes, every label and every message. This file only moves state.
 *
 * ---- THE ENDPOINT ----------------------------------------------------------
 * POST /apps/twc/v1/events (the app proxy, same origin, the path arrives on
 * `data-endpoint` so it is never written twice). The body is
 * multipart/form-data. The engine answers with the house envelope:
 *   200 -> { data: { status: "pending_review" } }
 *   4xx -> { error: { code, message } }
 * `message` is written for the visitor and is rendered word for word.
 *
 * ---- WHY THE CLIENT VALIDATES FIRST ---------------------------------------
 * The endpoint is rate limited: 2 seconds between sends and 20 an hour, and a
 * REJECTED send spends a slot too. A visitor who forgets a field must not burn
 * one. So every required field, the shape of the email address and every rule
 * about the photos are checked here, before the first byte goes out.
 *
 * ---- WHY THE PHOTOS ARE MADE SMALLER --------------------------------------
 * The platform in front of the engine refuses a body larger than about 4MB,
 * and it refuses it with its own error page — not with the envelope above. So
 * the visitor would see a page they cannot read. Instead: at most 5 photos,
 * JPEG/PNG/WebP only, and if the total is too large the photos are drawn to a
 * canvas at 2000px and re-encoded as JPEG. Three steps are tried; if the total
 * is still too large the visitor gets a plain sentence that tells them the
 * limit, and nothing is sent.
 *
 * ---- THE HONEYPOT ----------------------------------------------------------
 * `twc_confirm_url` is off-screen and out of the tab order. This file never
 * writes to it. A filled value means a robot: the send is dropped and the
 * confirmation is shown, so the robot learns nothing. The engine ignores
 * fields it does not know.
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-create-event')) return;

  var MAX_FILES = 5;

  /* The contract says 4MB combined. The budget below is smaller on purpose:
   * the text fields and the multipart boundaries ride in the same body. */
  var BUDGET_BYTES = 3.8 * 1024 * 1024;

  var ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  /* Tried in order until the total fits. Each step is a longest-edge limit and
   * a JPEG quality. */
  var SHRINK_STEPS = [
    { dim: 2000, quality: 0.85 },
    { dim: 1600, quality: 0.75 },
    { dim: 1200, quality: 0.7 },
  ];

  var REQUIRED_FIELDS = [
    'event_name',
    'event_location',
    'event_description',
    'event_datetime',
    'event_classification',
    'submitter_name',
    'submitter_email',
  ];

  // Deliberately loose: the engine is the authority on an address. This only
  // catches the typo that would otherwise spend a rate-limit slot.
  var EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function readCopy(root) {
    var node = root.querySelector('[data-twc-eventform-copy]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (err) {
      return {};
    }
  }

  function totalBytes(items) {
    var sum = 0;
    for (var i = 0; i < items.length; i++) sum += items[i].blob.size;
    return sum;
  }

  function formatSize(bytes) {
    var mb = bytes / (1024 * 1024);
    if (mb >= 0.1) return mb.toFixed(1) + ' MB';
    return Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  function isAllowedType(file) {
    var type = (file.type || '').toLowerCase();
    return ALLOWED_TYPES.indexOf(type) !== -1;
  }

  function decode(blob) {
    if (global.createImageBitmap) {
      return global.createImageBitmap(blob);
    }
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(blob);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('decode'));
      };
      img.src = url;
    });
  }

  function toBlob(canvas, quality) {
    return new Promise(function (resolve) {
      if (canvas.toBlob) {
        canvas.toBlob(
          function (blob) {
            resolve(blob);
          },
          'image/jpeg',
          quality
        );
        return;
      }
      resolve(null);
    });
  }

  function jpegName(name) {
    return String(name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
  }

  /* One photo, one step. The original is kept whenever the new copy is not
   * actually smaller — a small PNG can grow when it becomes a JPEG. */
  function shrinkOne(item, step) {
    return decode(item.blob)
      .then(function (source) {
        var width = source.width || source.naturalWidth;
        var height = source.height || source.naturalHeight;
        if (!width || !height) return item;

        var scale = Math.min(1, step.dim / Math.max(width, height));
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));

        var ctx = canvas.getContext('2d');
        if (!ctx) return item;
        ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
        if (source.close) source.close();

        return toBlob(canvas, step.quality).then(function (blob) {
          if (!blob || blob.size >= item.blob.size) return item;
          return { blob: blob, name: jpegName(item.name) };
        });
      })
      .catch(function () {
        return item;
      });
  }

  function shrinkAll(items, step) {
    var work = [];
    for (var i = 0; i < items.length; i++) work.push(shrinkOne(items[i], step));
    return Promise.all(work);
  }

  /* Joins the local number to the country prefix. A number already written in
   * full international form is left alone. */
  function composePhone(raw, prefix) {
    var value = String(raw || '').replace(/[\s()\-.]/g, '');
    if (!value) return '';
    if (value.charAt(0) === '+') return value;
    value = value.replace(/^0+/, '');
    if (!value) return '';
    return String(prefix || '') + value;
  }

  var TwcCreateEvent = class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.files = [];
      this.sending = false;
      this.scrollLocked = false;
      this.done = false;

      this.dialog = this.querySelector('[data-twc-eventform-dialog]');
      this.form = this.querySelector('[data-twc-eventform-form]');
      this.formPane = this.querySelector('[data-twc-eventform-pane="form"]');
      this.donePane = this.querySelector('[data-twc-eventform-pane="done"]');
      this.fileInput = this.querySelector('[data-twc-eventform-file]');
      this.dropzone = this.querySelector('[data-twc-eventform-dropzone]');
      this.fileList = this.querySelector('[data-twc-eventform-files]');
      this.formError = this.querySelector('[data-twc-eventform-form-error]');
      this.submitButton = this.querySelector('[data-twc-eventform-submit]');
      this.honeypot = this.querySelector('[data-twc-eventform-honeypot]');

      if (!this.dialog || !this.form) return;

      this.copy = readCopy(this);
      this.endpoint = this.getAttribute('data-endpoint') || '/apps/twc/v1/events';

      var self = this;
      var signal = this.controller.signal;

      this.addEventListener(
        'click',
        function (event) {
          var target = event.target;
          if (!target || typeof target.closest !== 'function') return;

          if (target.closest('[data-twc-eventform-open]')) {
            event.preventDefault();
            self.open();
            return;
          }

          if (target.closest('[data-twc-eventform-close]')) {
            event.preventDefault();
            self.close();
            return;
          }

          var drop = target.closest('[data-twc-eventform-drop]');
          if (drop) {
            event.preventDefault();
            self.removeFile(Number(drop.getAttribute('data-twc-eventform-drop')));
          }
        },
        { signal: signal }
      );

      // A click that lands on the dialog itself landed on the scrim: the panel
      // covers every other pixel. Nothing here is a compliance step, so the
      // scrim closes it — and so does Escape, which the dialog handles itself.
      this.dialog.addEventListener(
        'click',
        function (event) {
          if (event.target === self.dialog) self.close();
        },
        { signal: signal }
      );

      this.dialog.addEventListener(
        'close',
        function () {
          self.unlockScroll();
          if (self.done) self.reset();
        },
        { signal: signal }
      );

      this.form.addEventListener(
        'submit',
        function (event) {
          event.preventDefault();
          self.submit();
        },
        { signal: signal }
      );

      if (this.fileInput) {
        this.fileInput.addEventListener(
          'change',
          function () {
            self.addFiles(self.fileInput.files);
            // The element keeps its own list, so the control is emptied and the
            // same photo can be chosen again after it was removed.
            self.fileInput.value = '';
          },
          { signal: signal }
        );
      }

      if (this.dropzone) {
        ['dragenter', 'dragover'].forEach(function (name) {
          self.dropzone.addEventListener(
            name,
            function (event) {
              event.preventDefault();
              self.dropzone.setAttribute('data-twc-dragover', 'true');
            },
            { signal: signal }
          );
        });

        ['dragleave', 'dragend'].forEach(function (name) {
          self.dropzone.addEventListener(
            name,
            function () {
              self.dropzone.removeAttribute('data-twc-dragover');
            },
            { signal: signal }
          );
        });

        this.dropzone.addEventListener(
          'drop',
          function (event) {
            event.preventDefault();
            self.dropzone.removeAttribute('data-twc-dragover');
            if (event.dataTransfer) self.addFiles(event.dataTransfer.files);
          },
          { signal: signal }
        );
      }
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.unlockScroll();
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    /* ------------------------------------------------------------ dialog -- */

    open() {
      if (!this.dialog || this.dialog.open) return;
      this.dialog.showModal();
      this.lockScroll();
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;
      this.dialog.close();
    }

    lockScroll() {
      if (this.scrollLocked) return;
      document.documentElement.style.overflow = 'hidden';
      this.scrollLocked = true;
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      document.documentElement.style.overflow = '';
      this.scrollLocked = false;
    }

    /* ------------------------------------------------------------- photos -- */

    addFiles(list) {
      if (!list || !list.length) return;
      this.clearError('photos');

      var accepted = this.files.slice();
      var rejectedType = false;
      var tooMany = false;

      for (var i = 0; i < list.length; i++) {
        var file = list[i];
        if (!isAllowedType(file)) {
          rejectedType = true;
          continue;
        }
        if (accepted.length >= MAX_FILES) {
          tooMany = true;
          continue;
        }
        accepted.push({ blob: file, name: file.name });
      }

      this.files = accepted;
      this.renderFiles();

      if (rejectedType) this.showError('photos', this.copy.photo_type);
      else if (tooMany) this.showError('photos', this.copy.photo_count);
    }

    removeFile(index) {
      if (!(index >= 0) || index >= this.files.length) return;
      this.files.splice(index, 1);
      this.clearError('photos');
      this.renderFiles();
      if (this.fileInput) this.fileInput.focus();
    }

    renderFiles() {
      if (!this.fileList) return;

      while (this.fileList.firstChild) this.fileList.removeChild(this.fileList.firstChild);

      if (!this.files.length) {
        this.fileList.setAttribute('hidden', '');
        return;
      }

      for (var i = 0; i < this.files.length; i++) {
        var item = this.files[i];

        var row = document.createElement('li');
        row.className = 'twc-eventform__file-row';

        var name = document.createElement('span');
        name.className = 'twc-eventform__file-name';
        name.textContent = item.name + ' — ' + formatSize(item.blob.size);

        var drop = document.createElement('button');
        drop.type = 'button';
        drop.className = 'twc-eventform__file-drop';
        drop.setAttribute('data-twc-eventform-drop', String(i));
        drop.textContent = this.copy.remove || 'Remove';

        row.appendChild(name);
        row.appendChild(drop);
        this.fileList.appendChild(row);
      }

      this.fileList.removeAttribute('hidden');
    }

    prepareFiles() {
      var self = this;

      if (!this.files.length) return Promise.resolve({ files: [] });
      if (totalBytes(this.files) <= BUDGET_BYTES) return Promise.resolve({ files: this.files });

      var step = 0;

      function next(items) {
        if (step >= SHRINK_STEPS.length) return { error: self.copy.photo_size };
        var current = SHRINK_STEPS[step];
        step += 1;
        return shrinkAll(items, current).then(function (shrunk) {
          if (totalBytes(shrunk) <= BUDGET_BYTES) return { files: shrunk };
          return next(shrunk);
        });
      }

      return Promise.resolve(next(this.files));
    }

    /* --------------------------------------------------------- validation -- */

    field(name) {
      return this.form.querySelector('[name="' + name + '"]');
    }

    showError(name, message) {
      if (!message) return;
      var node = this.querySelector('[data-twc-eventform-error-for="' + name + '"]');
      if (node) {
        node.textContent = message;
        node.removeAttribute('hidden');
      }
      var input = this.field(name);
      if (input) input.setAttribute('aria-invalid', 'true');
      if (name === 'photos' && this.dropzone) this.dropzone.setAttribute('data-twc-invalid', 'true');
      if (name === 'event_datetime') {
        var box = this.querySelector('.twc-eventform__control--date');
        if (box) box.setAttribute('data-twc-invalid', 'true');
      }
    }

    clearError(name) {
      var node = this.querySelector('[data-twc-eventform-error-for="' + name + '"]');
      if (node) {
        node.textContent = '';
        node.setAttribute('hidden', '');
      }
      var input = this.field(name);
      if (input) input.removeAttribute('aria-invalid');
      if (name === 'photos' && this.dropzone) this.dropzone.removeAttribute('data-twc-invalid');
      if (name === 'event_datetime') {
        var box = this.querySelector('.twc-eventform__control--date');
        if (box) box.removeAttribute('data-twc-invalid');
      }
    }

    clearAllErrors() {
      for (var i = 0; i < REQUIRED_FIELDS.length; i++) this.clearError(REQUIRED_FIELDS[i]);
      this.clearError('photos');
      this.showFormError('');
    }

    showFormError(message) {
      if (!this.formError) return;
      if (!message) {
        this.formError.textContent = '';
        this.formError.setAttribute('hidden', '');
        return;
      }
      this.formError.textContent = message;
      this.formError.removeAttribute('hidden');
    }

    validate() {
      var required = this.copy.required || {};
      var first = null;

      for (var i = 0; i < REQUIRED_FIELDS.length; i++) {
        var name = REQUIRED_FIELDS[i];
        var input = this.field(name);
        if (!input) continue;
        if (String(input.value || '').trim()) continue;
        this.showError(name, required[name]);
        if (!first) first = input;
      }

      var email = this.field('submitter_email');
      if (email && !first) {
        var value = String(email.value || '').trim();
        if (value && !EMAIL_SHAPE.test(value)) {
          this.showError('submitter_email', this.copy.email);
          first = email;
        }
      }

      if (this.files.length > MAX_FILES) {
        this.showError('photos', this.copy.photo_count);
        if (!first) first = this.fileInput;
      }

      if (first && first.focus) first.focus();

      return !first;
    }

    /* ------------------------------------------------------------- sending -- */

    setBusy(busy) {
      this.sending = busy;
      if (!this.submitButton) return;
      var label = busy
        ? this.submitButton.getAttribute('data-twc-label-busy')
        : this.submitButton.getAttribute('data-twc-label');
      if (label) this.submitButton.textContent = label;
      if (busy) this.submitButton.setAttribute('disabled', '');
      else this.submitButton.removeAttribute('disabled');
    }

    body(files) {
      var data = new FormData();

      for (var i = 0; i < REQUIRED_FIELDS.length; i++) {
        var name = REQUIRED_FIELDS[i];
        var input = this.field(name);
        data.set(name, input ? String(input.value || '').trim() : '');
      }

      var phone = this.querySelector('[data-twc-eventform-phone]');
      if (phone) {
        var composed = composePhone(phone.value, phone.getAttribute('data-twc-prefix'));
        if (composed) data.set('submitter_phone', composed);
      }

      for (var f = 0; f < files.length; f++) {
        data.append('photos', files[f].blob, files[f].name);
      }

      return data;
    }

    submit() {
      var self = this;
      if (this.sending) return;

      this.clearAllErrors();
      if (!this.validate()) return;

      // A robot filled the field no visitor can reach. Nothing is sent, and
      // the confirmation is shown so the robot learns nothing.
      if (this.honeypot && String(this.honeypot.value || '').trim()) {
        this.showDone();
        return;
      }

      if (global.Shopify && global.Shopify.designMode) {
        this.showFormError(this.copy.editor);
        return;
      }

      this.setBusy(true);

      this.prepareFiles()
        .then(function (result) {
          if (result.error) {
            self.showError('photos', result.error);
            self.setBusy(false);
            return null;
          }

          return global.fetch(self.endpoint, {
            method: 'POST',
            body: self.body(result.files),
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: self.controller.signal,
          });
        })
        .then(function (response) {
          if (!response) return null;
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (payload) {
              return { ok: response.ok, payload: payload };
            });
        })
        .then(function (result) {
          if (!result) return;
          self.setBusy(false);

          if (result.ok && result.payload && result.payload.data) {
            self.showDone();
            return;
          }

          var error = result.payload && result.payload.error;
          self.showFormError((error && error.message) || self.copy.network);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          self.setBusy(false);
          self.showFormError(self.copy.network);
        });
    }

    showDone() {
      this.done = true;
      if (this.formPane) this.formPane.setAttribute('hidden', '');
      if (this.donePane) this.donePane.removeAttribute('hidden');
      var target = this.querySelector('[data-twc-eventform-done]');
      if (target && target.focus) target.focus();
    }

    /* The form comes back only after the visitor closes the confirmation, so
     * the confirmation never blinks away under their eyes. */
    reset() {
      this.done = false;
      this.files = [];
      this.renderFiles();
      this.clearAllErrors();
      this.setBusy(false);
      if (this.form && this.form.reset) this.form.reset();
      if (this.donePane) this.donePane.setAttribute('hidden', '');
      if (this.formPane) this.formPane.removeAttribute('hidden');
    }
  };

  if (global.customElements) {
    global.customElements.define('twc-create-event', TwcCreateEvent);
  }
})(window);
